import React, { FC, useState } from "react";
import { defineMessages, useIntl } from "react-intl";

import { useErrorController } from "@natera/platform/lib/hooks";
import { useMdcSelectScrollFixAll } from "@app/preBill/hooks";
import { Form } from "@natera/form";

import { GuestValidationInput } from "@app/preBill/models";
import { ButtonNoShadow } from "@app/components/buttonNoShadow";
import { DOBFormValidateDay, validateYear } from "@app/utils/dateHelper";

import "./dateOfBirthVerification.scss";
import DateOfBirthVerificationForm, {
  Month,
} from "./dateOfBirthVerificationForm";

const messages = defineMessages({
  dateOfBirthVerificationVerify: {
    id: "dateOfBirthVerificationVerify",
    defaultMessage: "Verify",
  },
  dateOfBirthVerificationEmptyValue: {
    id: "dateOfBirthVerificationEmptyValue",
    defaultMessage: "This information is required",
  },
  dateOfBirthVerificationInvalidDay: {
    id: "dateOfBirthVerificationInvalidDay",
    defaultMessage: "Invalid or incomplete value.",
  },
  dateOfBirthVerificationInvalidYear: {
    id: "dateOfBirthVerificationInvalidYear",
    defaultMessage: "Incorrect date of birth. Please re-enter",
  },
});

interface DateOfBirthProps {
  loading: boolean;
  onSubmit: (data: GuestValidationInput) => void;
}

const DateOfBirthVerification: FC<DateOfBirthProps> = ({
  loading,
  onSubmit,
}) => {
  useMdcSelectScrollFixAll();
  const intl = useIntl();
  const {
    getValidationError,
    clearValidationError,
    clearErrors,
    setValidationError,
  } = useErrorController();

  const [month, setMonth] = useState<Month | null>(null);
  const [day, setDay] = useState<string>("");
  const [year, setYear] = useState<string>("");

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    clearErrors();

    const formData = new FormData(event.currentTarget);
    const monthValue = month?.value?.toString() ?? "";
    const dayValue = formData.get("dateOfBirthDay")?.toString();
    const yearValue = formData.get("dateOfBirthYear")?.toString();

    const setFieldError = (
      field: string,
      messageKey: keyof typeof messages
    ) => {
      setValidationError(field, intl.formatMessage(messages[messageKey]));
    };

    if (!monthValue) {
      setFieldError("dateOfBirthMonth", "dateOfBirthVerificationEmptyValue");
      return;
    }

    if (!dayValue) {
      setFieldError("dateOfBirthDay", "dateOfBirthVerificationEmptyValue");
      return;
    }

    if (!yearValue) {
      setFieldError("dateOfBirthYear", "dateOfBirthVerificationEmptyValue");
      return;
    }

    if (!validateYear(yearValue)) {
      setFieldError("dateOfBirthYear", "dateOfBirthVerificationInvalidYear");
      return;
    }

    if (!DOBFormValidateDay(dayValue, parseInt(monthValue, 10), yearValue)) {
      setValidationError(
        "dateOfBirthDay",
        intl.formatMessage(messages.dateOfBirthVerificationInvalidDay)
      );
      setFieldError("dateOfBirthDay", "dateOfBirthVerificationInvalidDay");
      return;
    }

    const dateOfBirth: GuestValidationInput = {
      month: parseInt(monthValue, 10),
      day: parseInt(dayValue, 10),
      year: parseInt(yearValue, 10),
    };

    onSubmit(dateOfBirth);
  };

  return (
    <div className="date-of-birth-form">
      <section className="form__container">
        <Form
          onSubmit={handleSubmit}
          buttons={
            <ButtonNoShadow type="submit" raised loading={loading}>
              {intl.formatMessage(messages.dateOfBirthVerificationVerify)}
            </ButtonNoShadow>
          }
        >
          <DateOfBirthVerificationForm
            month={month}
            setMonth={setMonth}
            day={day}
            setDay={setDay}
            year={year}
            setYear={setYear}
            getValidationError={getValidationError}
            clearValidationError={clearValidationError}
          />
        </Form>
      </section>
    </div>
  );
};

export default DateOfBirthVerification;
